import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { getBranches } from '@/app/modules/ECommerce/pages/branch/services';
import useMiniPOSPermission from '@/app/modules/ECommerce/pages/miniPOS/hooks/useMiniPOSPermission';
import PERMISSION from '@/app/modules/ECommerce/pages/miniPOS/utils/permission.constant';

const STORAGE_KEY = 'selected_branch_id';

export const useBranches = () => {
  const history = useHistory();
  const { shop, user } = useSelector(state => state.auth);
  const { can } = useMiniPOSPermission();

  const shouldSelectBranch = Boolean(shop?.maxNumberOfBranches && can(PERMISSION.POS_MANAGE_BRANCHES));

  const getStoredBranchId = () => sessionStorage.getItem(STORAGE_KEY);

  const [selectedBranchId, setSelectedBranchId] = useState(() => getStoredBranchId());

  const syncBranchFromStorage = useCallback(() => {
    setSelectedBranchId(getStoredBranchId());
  }, []);

  useEffect(() => {
    window.addEventListener('storage', syncBranchFromStorage);
    window.addEventListener('branch-change', syncBranchFromStorage);

    return () => {
      window.removeEventListener('storage', syncBranchFromStorage);
      window.removeEventListener('branch-change', syncBranchFromStorage);
    };
  }, [syncBranchFromStorage]);

  const { data: branches = [], isFetched, isLoading } = useQuery(
    ['branches', shop?._id],
    async () => {
      const res = await getBranches();
      return res.data.branches;
    },
    {
      enabled: shouldSelectBranch,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );
  const allowedBranchIds = useMemo(() => user?.assignedBranches?.map(b => b._id) ?? [], [user?.assignedBranches]);

  const filteredBranches = useMemo(() => {
    if (!branches.length) return [];

    return branches.filter(
      branch => branch.isActive && (allowedBranchIds.length === 0 || allowedBranchIds.includes(branch._id))
    );
  }, [branches, allowedBranchIds]);

  const selectedBranch = useMemo(() => {
    if (!selectedBranchId) return null;
    return filteredBranches.find(branch => branch._id === selectedBranchId) ?? null;
  }, [filteredBranches, selectedBranchId]);

  const updateSelectedBranch = useCallback(branch => {
    if (!branch) {
      sessionStorage.removeItem(STORAGE_KEY);
      setSelectedBranchId(null);
    } else {
      sessionStorage.setItem(STORAGE_KEY, branch._id);
      setSelectedBranchId(branch._id);
    }

    window.dispatchEvent(new CustomEvent('branch-change'));
  }, []);

  useEffect(() => {
    // Only force branch selection when there is at least one active/allowed branch to pick.
    // Guard on filteredBranches (already isActive-filtered), not raw branches, otherwise a shop
    // whose branches are all inactive would be redirected to an empty select-branch page.
    if (!shouldSelectBranch || !isFetched || !shop?.seqId || !filteredBranches.length) return;

    const currentPath = history.location.pathname;
    const selectBranchPath = `/shop/${shop.seqId}/mini-pos/branch`;

    if (currentPath === selectBranchPath) return;

    if (!selectedBranchId && filteredBranches.length === 1) {
      updateSelectedBranch(filteredBranches[0]);
      return;
    }

    const isValidSelection = Boolean(selectedBranch);

    if (!isValidSelection) {
      updateSelectedBranch(null);

      history.replace({
        pathname: selectBranchPath,
        state: { from: currentPath }
      });
    }
  }, [
    shouldSelectBranch,
    isFetched,
    filteredBranches,
    selectedBranch,
    selectedBranchId,
    history,
    shop?.seqId,
    updateSelectedBranch
  ]);

  return {
    branches,
    filteredBranches,
    selectedBranch,
    selectedBranchId,
    setSelectedBranch: updateSelectedBranch,
    shouldSelectBranch,
    isLoading
  };
};
